// src/pages/OnboardingPage.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Onboarding personnalisé & parcours de démarrage (spec « ARTKIV — Onboarding »).
//
// Principe fondateur : ne JAMAIS redemander une information que le système connaît
// déjà (abonnement, modules, rôle, langue, pays, partenaire). Cette page ne
// configure pas le logiciel : elle comprend le besoin pour personnaliser l'UX.
//
// Elle LIT le profil (rôle / plan / foundation_mode / partner_type déjà posés à
// l'inscription) et ne pose que les questions de personnalisation propres au
// métier. Les réponses sont écrites via la RPC `save_onboarding` (SECURITY
// DEFINER) — jamais par un UPDATE direct : aucune colonne sensible n'est touchée.
//
// i18n : tout le texte vient du namespace `onboarding.*` (FR/EN/DE). CONTENT ne
// porte que la STRUCTURE (ids de questions, clés d'options, route de 1re action).
import React, { useEffect, useMemo, useState } from "react";
import "./OnboardingPage.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import { needsOnboarding, startRouteForUser } from "../utils/startRouteForUser";
import { resolveProfileType, isDepositPrimary, accountHasContent, metierHomeRoute } from "../utils/onboardingProfile";

const PLAN_LABELS = {
  standard: "Standard", essential: "Essential", premium: "Premium", pro: "Pro",
  advisor: "Art Advisor", advisor_premium: "Art Advisor Premium",
  family_office: "Family Office", foundation: "Fondation",
};
function planLabel(plan) {
  if (!plan) return null;
  const key = String(plan).toLowerCase();
  return PLAN_LABELS[key] || key.charAt(0).toUpperCase() + key.slice(1);
}

// STRUCTURE uniquement (aucun texte affiché ici — tout passe par t()).
// Chaque question : { id, type, opts? } ; type = "choice" | "text" | "boolean".
const CONTENT = {
  collectionneur: {
    // Écran de choix : self-service (4 chemins) OU « faites-le pour moi » (dépôt assisté).
    route: "/commencer",
    questions: [
      { id: "collection_size", type: "choice", opts: ["lt20", "b20_100", "b100_500", "gt500"] },
      { id: "documents_location", type: "choice", opts: ["paper", "computer", "cloud", "scattered"] },
      { id: "main_goal", type: "choice", opts: ["inventory", "transmission", "insure", "market"] },
      { id: "wants_support", type: "boolean" },
    ],
  },
  advisor: {
    route: "/expert/dashboard",
    questions: [
      { id: "already_clients", type: "boolean" },
      { id: "clients_count", type: "choice", opts: ["b1_5", "b6_20", "b21_50", "gt50"] },
      { id: "has_inventories", type: "boolean" },
    ],
  },
  family_office: {
    route: "/family-office/manage",
    questions: [
      { id: "families_count", type: "choice", opts: ["b1_3", "b4_10", "gt10"] },
      { id: "works_with_experts", type: "boolean" },
      { id: "create_first_family", type: "boolean" },
    ],
  },
  fondation: {
    route: "/foundation/dashboard",
    questions: [
      { id: "foundation_name", type: "text" },
      { id: "collections_count", type: "choice", opts: ["one", "b2_5", "gt5"] },
      { id: "has_inventory", type: "boolean" },
      { id: "collaborators_count", type: "choice", opts: ["solo", "b2_5", "gt5"] },
    ],
  },
  notaire: {
    route: "/notaire",
    questions: [
      { id: "handles_art_successions", type: "boolean" },
      { id: "create_first_dossier", type: "boolean" },
    ],
  },
  assureur: {
    route: "/partner/insurance",
    questions: [
      { id: "collections_count", type: "choice", opts: ["b1_5", "b6_20", "gt20"] },
      { id: "has_portfolio", type: "boolean" },
    ],
  },
};

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, profile, loading, profileLoading, isAuthenticated } = useAuth();

  const [step, setStep] = useState(0); // 0 = bienvenue, 1..n = questions, "done"
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const profileType = useMemo(() => resolveProfileType(profile), [profile]);
  const content = CONTENT[profileType] || CONTENT.collectionneur;
  const questions = content.questions;

  useEffect(() => { document.title = t("onboarding.welcome.eyebrow"); }, [t]);

  useEffect(() => {
    if (loading || profileLoading) return;
    if (!isAuthenticated) { navigate("/login", { replace: true }); return; }
    if (!profile) return;
    if (!needsOnboarding(profile)) {
      navigate(startRouteForUser(profile), { replace: true });
      return;
    }
    // Compte déjà rempli (invité, migré, admin, legacy) : pas de parcours
    // « premiers pas » — on l'envoie directement à son espace.
    let cancelled = false;
    (async () => {
      if (await accountHasContent(profileType) && !cancelled) {
        navigate(metierHomeRoute(profileType), { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [loading, profileLoading, isAuthenticated, profile, navigate, profileType]);

  if (loading || profileLoading || !profile) {
    return <Shell><p style={pStyle}>{t("onboarding.reco.loading")}</p></Shell>;
  }

  const firstName = profile.first_name || user?.user_metadata?.first_name || "";
  const setAnswer = (id, value) => setAnswers((prev) => ({ ...prev, [id]: value }));

  const spaceText = () => {
    if (profileType === "collectionneur") {
      const p = planLabel(profile?.plan);
      return p
        ? t("onboarding.welcome.space.collectionneurPlan", { plan: p })
        : t("onboarding.welcome.space.collectionneurGeneric");
    }
    return t(`onboarding.welcome.space.${profileType}`);
  };

  const handleFinish = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const { error } = await supabase.rpc("save_onboarding", {
        p_answers: { profile_type: profileType, ...answers },
      });
      if (error) throw error;
      setStep("done");
    } catch (e) {
      console.error("[Onboarding] save error:", e);
      setSaveError(t("onboarding.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const goToFirstAction = () => window.location.assign(content.route);

  // ── Écran bienvenue ─────────────────────────────────────────────────────────
  if (step === 0) {
    return (
      <Shell>
        <div className="ob-stagger" key="welcome">
          <p style={eyebrow}>{t("onboarding.welcome.eyebrow")}</p>
          <h1 style={h1Style}>
            {firstName
              ? t("onboarding.welcome.helloName", { name: firstName })
              : t("onboarding.welcome.helloNoName")}
          </h1>
          <div style={spaceBox}>{spaceText()}</div>
          <p style={pStyle}>{t("onboarding.welcome.intro")}</p>
          <button style={ctaPrimary} onClick={() => setStep(1)}>{t("onboarding.welcome.start")}</button>
        </div>
      </Shell>
    );
  }

  // ── Écran final : première action ───────────────────────────────────────────
  if (step === "done") {
    return (
      <Shell>
        <div className="ob-stagger" key="done">
        <p style={eyebrow}>{t("onboarding.done.eyebrow")}</p>
        <h1 style={h1Style}>{t("onboarding.done.title")}</h1>
        <div style={missionBox}>
          <p style={missionLabel}>{t("onboarding.done.actionLabel")}</p>
          <p style={missionText}>{t(`onboarding.actions.${profileType}.title`)}</p>
          <button style={ctaPrimary} onClick={goToFirstAction}>
            {t(`onboarding.actions.${profileType}.cta`)}
          </button>
          {/* Métiers non-collectionneur : reprendre l'existant = entrée dans le
              pipeline dépôt → analyse → recommandation (spec « migration assistée »). */}
          {!isDepositPrimary(profileType) && (
            <button
              style={{ ...ctaGhost, display: "block", marginTop: "0.75rem" }}
              onClick={() => window.location.assign("/onboarding/deposit")}
            >
              {t("onboarding.done.importExisting")}
            </button>
          )}
        </div>
        </div>
      </Shell>
    );
  }

  // ── Questions ───────────────────────────────────────────────────────────────
  const qIndex = step - 1;
  const question = questions[qIndex];
  const isLast = qIndex === questions.length - 1;
  const value = answers[question.id];
  const qBase = `onboarding.questions.${profileType}.${question.id}`;

  const next = () => (isLast ? handleFinish() : setStep(step + 1));
  const back = () => setStep(step - 1);

  return (
    <Shell>
      <div style={progressRow}>
        <span style={eyebrow}>
          {t("onboarding.progress", { current: qIndex + 1, total: questions.length })}
        </span>
        <div style={progressTrack}>
          <div style={{ ...progressFill, width: `${((qIndex + 1) / questions.length) * 100}%` }} />
        </div>
      </div>

      <div className="ob-in" key={step}>
      <h2 style={qTitle}>{t(`${qBase}.label`)}</h2>

      <div style={{ margin: "1.25rem 0 1.75rem" }}>
        <QuestionInput question={question} qBase={qBase} value={value} onChange={setAnswer} t={t} />
      </div>

      {saveError && <p style={errorStyle}>{saveError}</p>}

      <div style={navRow}>
        {qIndex > 0 ? (
          <button style={ctaGhost} onClick={back} disabled={saving}>{t("onboarding.back")}</button>
        ) : <span />}
        <div style={{ display: "flex", gap: "0.6rem" }}>
          <button style={ctaGhost} onClick={next} disabled={saving}>{t("onboarding.skip")}</button>
          <button style={ctaPrimary} onClick={next} disabled={saving}>
            {saving ? t("onboarding.saving") : isLast ? t("onboarding.finish") : t("onboarding.continue")}
          </button>
        </div>
      </div>
      </div>
    </Shell>
  );
}

function QuestionInput({ question, qBase, value, onChange, t }) {
  const { id, type, opts, placeholder } = question;

  if (type === "boolean") {
    return (
      <div style={{ display: "flex", gap: "0.6rem" }}>
        {[{ label: t("onboarding.yes"), val: true }, { label: t("onboarding.no"), val: false }].map((opt) => (
          <button key={opt.label} onClick={() => onChange(id, opt.val)} style={value === opt.val ? optionSelected : optionButton}>
            {opt.label}
          </button>
        ))}
      </div>
    );
  }

  if (type === "choice") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
        {opts.map((optKey) => {
          const label = t(`${qBase}.opts.${optKey}`);
          return (
            <button key={optKey} onClick={() => onChange(id, optKey)} style={value === optKey ? optionSelected : optionButton}>
              {label}
            </button>
          );
        })}
      </div>
    );
  }

  // text
  return (
    <input
      type="text"
      value={value ?? ""}
      placeholder={placeholder || t(`${qBase}.placeholder`, { defaultValue: "" })}
      onChange={(e) => onChange(id, e.target.value)}
      style={inputStyle}
    />
  );
}

function Shell({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(160deg,#f9f7f4,#f1ede6)", display: "flex", alignItems: "center", justifyContent: "center", padding: "2.5rem 1.25rem", fontFamily: "Georgia, serif" }}>
      <div style={{ width: "100%", maxWidth: 560, background: "#fff", border: "1px solid #e5e0d8", borderRadius: 18, padding: "2.5rem" }}>
        {children}
      </div>
    </div>
  );
}

const eyebrow = { fontSize: "0.72rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "#8a8378", margin: 0 };
const h1Style = { fontSize: "1.8rem", fontWeight: 700, lineHeight: 1.2, color: "#1a1a1a", margin: "0.6rem 0 1.25rem" };
const qTitle = { fontSize: "1.35rem", fontWeight: 700, lineHeight: 1.3, color: "#1a1a1a", margin: "1.25rem 0 0" };
const pStyle = { fontSize: "0.95rem", lineHeight: 1.6, color: "#4a453d", margin: "0 0 1.5rem" };
const spaceBox = { background: "#eef5f1", border: "1px solid #cfe3d8", borderRadius: 12, padding: "0.9rem 1.1rem", marginBottom: "1.25rem", color: "#2e7d55", fontWeight: 700 };
const missionBox = { background: "#fff", border: "1px solid #e5e0d8", borderRadius: 12, padding: "1.5rem", marginTop: "0.5rem" };
const missionLabel = { fontSize: "0.72rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "#c9a961", margin: "0 0 0.6rem" };
const missionText = { fontSize: "1.05rem", lineHeight: 1.5, color: "#1a1a1a", margin: "0 0 1.25rem" };
const ctaPrimary = { display: "inline-block", background: "#1a1a1a", color: "#f9f7f4", border: "none", cursor: "pointer", padding: "0.85rem 1.75rem", borderRadius: 10, fontSize: "0.95rem", fontFamily: "inherit" };
const ctaGhost = { display: "inline-block", background: "transparent", color: "#6b6459", border: "1px solid #d8d2c8", cursor: "pointer", padding: "0.85rem 1.4rem", borderRadius: 10, fontSize: "0.9rem", fontFamily: "inherit" };
const optionButton = { textAlign: "left", background: "#faf8f5", color: "#1a1a1a", border: "1px solid #e5e0d8", cursor: "pointer", padding: "0.85rem 1.1rem", borderRadius: 10, fontSize: "0.95rem", fontFamily: "inherit" };
const optionSelected = { ...optionButton, background: "#1a1a1a", color: "#f9f7f4", borderColor: "#1a1a1a" };
const inputStyle = { width: "100%", boxSizing: "border-box", background: "#faf8f5", color: "#1a1a1a", border: "1px solid #e5e0d8", padding: "0.85rem 1.1rem", borderRadius: 10, fontSize: "0.95rem", fontFamily: "inherit" };
const progressRow = { display: "flex", flexDirection: "column", gap: "0.5rem" };
const progressTrack = { width: "100%", height: 4, background: "#eee8df", borderRadius: 999, overflow: "hidden" };
const progressFill = { height: "100%", background: "#c9a961", borderRadius: 999, transition: "width 0.25s ease" };
const navRow = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.6rem" };
const errorStyle = { color: "#b42318", fontSize: "0.85rem", margin: "0 0 0.75rem" };
